// ============================================================================
// Item 896 (TCSD-0510): which unit a sub change's pay is in.
//
// The request keeps ONE pay figure, `incoming_pay_rate`. Its unit is read from
// the OUTGOING line: a line that pays its sub by the hour takes the new pay per
// hour; every other line, per month, exactly as before. Never from the size of
// the number, never from a new field. The per-hour words themselves live once,
// in src/shared/rate-basis.ts — this file only picks between them.
// ============================================================================

import {
  NO_SET_HOURS_SENTENCE,
  aboutAMonth,
  paysSubByTheHour,
  perHourShort,
  scheduleMonthlyEstimate,
} from '../../shared/rate-basis';

/** The outgoing line, as much of it as the pay unit needs. */
export interface PayLine {
  sub_rate_type?: string | null;
  sub_hours_per_day?: number | null;
  service_days_per_week?: number | null;
}

/**
 * A request with no pay yet. Ben's 21 August rule: the incoming sub's pay is
 * needed at the changeover and is never carried over from the outgoing sub.
 */
export const PAY_NOT_SET_TEXT = 'Not set yet — needed at the changeover';

/** "$/hr" on a line that pays by the hour, "$/mo" otherwise. */
export function payUnit(line: PayLine | null | undefined): '$/hr' | '$/mo' {
  return paysSubByTheHour(line) ? '$/hr' : '$/mo';
}

/**
 * The request's pay as the request page and the approval queue show it:
 * "$20.00/hr" on an hourly line; "$1450/mo" on a monthly line, written exactly
 * as those screens always wrote it; the not-set sentence when there is none.
 */
export function incomingPayText(rate: number | string | null | undefined, line: PayLine | null | undefined): string {
  if (rate == null || rate === '') return PAY_NOT_SET_TEXT;
  return paysSubByTheHour(line) ? perHourShort(Number(rate)) : `$${rate}/mo`;
}

/**
 * Under the changeover's pay box on an hourly line: what the typed hourly pay
 * comes to in a month at the line's set hours, or why there is no monthly
 * figure. Null on a monthly line, and on an hourly line with hours set but no
 * pay typed yet. An estimate worked out here and never saved.
 */
export function hourlyMonthNote(rate: number | null, line: PayLine | null | undefined): string | null {
  if (!paysSubByTheHour(line)) return null;
  if (line?.sub_hours_per_day == null || line?.service_days_per_week == null) return NO_SET_HOURS_SENTENCE;
  const est = scheduleMonthlyEstimate(rate, line.sub_hours_per_day, line.service_days_per_week);
  return est == null ? null : `${aboutAMonth(est)} at its set hours.`;
}

/** The request form's outgoing line, as much of it as the pay box needs. */
export interface FormPayLine extends PayLine {
  sub_rate_monthly?: number | null;
  sub_hourly_rate?: number | null;
}

/** The request form's pay box placeholder: the outgoing line's pay, in its unit. */
export function payPlaceholder(line: FormPayLine | null | undefined): string {
  if (paysSubByTheHour(line)) {
    return line?.sub_hourly_rate != null
      ? `Current rate: ${perHourShort(Number(line.sub_hourly_rate))}`
      : 'Hourly rate for the incoming sub';
  }
  return line?.sub_rate_monthly != null
    ? `Current rate: $${line.sub_rate_monthly}/mo`
    : 'Monthly rate for the incoming sub';
}

/**
 * The sentence under the request form's pay box. Optional at submit, needed at
 * the changeover, never carried over (Ben's 21 August rule; the old sentence,
 * "Leave blank to keep the current rate", said the opposite — Ben 2026-10-19).
 */
export function payHint(line: FormPayLine | null | undefined): string {
  if (paysSubByTheHour(line)) {
    return 'Needed at the changeover and never carried over: enter the incoming subcontractor’s own contract rate per hour.'
      + (line?.sub_hourly_rate != null ? ` Outgoing contract rate: ${perHourShort(Number(line.sub_hourly_rate))}.` : '');
  }
  return 'Needed at the changeover and never carried over: enter the incoming subcontractor’s own monthly contract rate.'
    + (line?.sub_rate_monthly != null ? ` Outgoing contract rate: $${line.sub_rate_monthly}/mo.` : '');
}
